import { invalidateInventory } from '../features/collection/inventoryInvalidation';
import { createOperationId } from '../domain/operationId';

export interface MutationScope {
  collectionId: string;
  itemIds?: readonly string[];
}
export const MUTATION_PENDING =
  'An earlier update is awaiting confirmation. Retry it before changing these items or their locations.';
export interface PendingMutation {
  id: string;
  userId: string;
  label: string;
  scopes: readonly MutationScope[];
  busy: boolean;
  uncertain: boolean;
  retry?: () => Promise<unknown>;
}
const overlaps = (left: MutationScope, right: MutationScope) =>
  left.collectionId === right.collectionId &&
  (!left.itemIds ||
    !right.itemIds ||
    left.itemIds.some((id) => right.itemIds!.includes(id)));

/** A transport error is not proof that a mutation failed. Keep its immutable command. */
export function definitiveMutationRejection(cause: unknown): boolean {
  const error = cause as {
    code?: string;
    details?: { code?: string };
    cause?: unknown;
  };
  if (error?.cause) return definitiveMutationRejection(error.cause);
  return (
    [
      'invalid-request',
      'role-required',
      'membership-required',
      'authentication-required',
      'verification-required',
      'collection-not-found',
      'collection-unavailable',
      'operation-conflict',
      'capacity-exceeded',
      'insufficient-credits',
    ].includes(error?.details?.code || '') ||
    [
      'permission-denied',
      'invalid-argument',
      'unauthenticated',
      'not-found',
    ].some(
      (code) => error?.code === code || error?.code === `functions/${code}`,
    )
  );
}

export function createInventoryMutationCoordinator() {
  const entries = new Map<
    string,
    PendingMutation & { intent?: string; promise?: Promise<unknown> }
  >();
  const listeners = new Set<() => void>();
  let snapshot: PendingMutation[] = [];
  const publish = () => {
    snapshot = [...entries.values()].map((entry) => ({ ...entry }));
    listeners.forEach((listener) => listener());
  };
  const conflict = (
    userId: string,
    scopes: readonly MutationScope[],
    except?: string,
  ) =>
    [...entries.values()].some(
      (entry) =>
        entry.userId === userId &&
        entry.id !== except &&
        entry.scopes.some((left) =>
          scopes.some((right) => overlaps(left, right)),
        ),
    );
  const acquire = (entry: PendingMutation) => {
    if (
      (entries.has(entry.id) && entries.get(entry.id) !== entry) ||
      conflict(entry.userId, entry.scopes, entry.id)
    )
      throw Object.assign(new Error(MUTATION_PENDING), {
        code: 'mutation-pending',
      });
    entries.set(entry.id, entry);
    publish();
  };
  const release = (id: string) => {
    entries.delete(id);
    publish();
  };
  return {
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getSnapshot: () => snapshot,
    conflict,
    acquire,
    release,
    // A remounted controller resumes ownership of recovery and its feedback.
    resume: (id: string, retry: () => Promise<unknown>) => {
      const entry = entries.get(id);
      if (entry) {
        entry.retry = retry;
        entry.uncertain = false;
        publish();
      }
    },
    markUncertain: (id: string) => {
      const entry = entries.get(id);
      if (entry) {
        entry.uncertain = true;
        publish();
      }
    },
    async run<T>(
      userId: string,
      scopes: readonly MutationScope[],
      intent: string,
      label: string,
      execute: (operationId: string) => Promise<T>,
      operationId = createOperationId(),
    ): Promise<T> {
      const existing = [...entries.values()].find(
        (entry) => entry.userId === userId && entry.intent === intent,
      );
      if (existing)
        return (existing.promise || existing.retry!()) as Promise<T>;
      const entry: PendingMutation & {
        intent: string;
        promise?: Promise<unknown>;
      } = {
        id: operationId,
        userId,
        scopes,
        intent,
        label,
        busy: false,
        uncertain: false,
      };
      acquire(entry);
      const attempt = (): Promise<T> => {
        if (entry.promise) return entry.promise as Promise<T>;
        entry.busy = true;
        entry.promise = Promise.resolve()
          .then(() => execute(entry.id))
          .catch((cause) => {
            const error = cause as {
              code?: string;
              details?: { retryable?: boolean };
            };
            if (
              error?.details?.retryable ||
              /unavailable|deadline-exceeded|network/.test(error?.code || '')
            ) {
              // Record uncertainty before a second attempt: a rejection on replay cannot
              // establish that the first attempt never committed.
              entry.uncertain = true;
              return execute(entry.id);
            }
            throw cause;
          })
          .then(
            (value) => {
              release(entry.id);
              for (const collectionId of new Set(
                scopes.map((scope) => scope.collectionId),
              )) {
                invalidateInventory({
                  userId,
                  collectionId,
                  resources: ['items', 'locations'],
                });
              }
              return value;
            },
            (cause) => {
              if (!entry.uncertain && definitiveMutationRejection(cause))
                release(entry.id);
              else entry.uncertain = true;
              throw cause;
            },
          )
          .finally(() => {
            entry.busy = false;
            entry.promise = undefined;
            publish();
          });
        publish();
        return entry.promise as Promise<T>;
      };
      entry.retry = attempt;
      return attempt();
    },
  };
}

export const inventoryMutations = createInventoryMutationCoordinator();
// Blob identity prevents a replacement photo from being mistaken for an uncertain prior save.
const blobs = new WeakMap<Blob, string>();
export const mutationBlobKey = (blob: Blob) => {
  if (!blobs.has(blob)) blobs.set(blob, createOperationId());
  return blobs.get(blob)!;
};
